import {
  type MapDefinition,
  type Region,
  type Territory,
  regionId,
  territoryId,
} from './schema.js';

const CORAL = regionId('coral');
const DEEPFIN = regionId('deepfin');
const SHARKBAY = regionId('sharkbay');
const TIDEWALL = regionId('tidewall');

// Layout: regions occupy distinct compass quadrants of the map zone so
// each one reads as a "continent." Within a region the territories sit
// flush so they share edges; cross-region adjacencies are drawn as lines
// between the closest pair of squares from each side.
//
//                  ┌──┬──┐
//                  │NW│NE│        Coral Reach (north)
//                  ├──┼──┤
//                  │SW│SE│
//                  └──┴──┘
//       ┌──┐                ┌──┐
//       │N │                │N │
//       ├──┤                ├──┤
// Tide  │M │                │M │  Deepfin
//       ├──┤                ├──┤
//       │S │                │S │
//       └──┘                └──┘
//                  ┌──┬──┬──┐
//                  │W │M │E │     Sharkbay (south)
//                  └──┴──┴──┘
//
// Cross-region bridges (4 total, mirrored left/right):
//   coral_sw ↔ tidewall_n   |   coral_se ↔ deepfin_n   (top)
//   tidewall_s ↔ sharkbay_w |   deepfin_s ↔ sharkbay_e (bottom)
// Coral NW and Coral NE are interior tiles (no cross-region exits).
//
// The four regions form a ring around an empty center — hence the name.
//
// Map zone runs y=250..850 (600 tall, half the design canvas after the
// HUD grew to fit the full-width logo).

const T = {
  coralNW: territoryId('coral_nw'),
  coralNE: territoryId('coral_ne'),
  coralSW: territoryId('coral_sw'),
  coralSE: territoryId('coral_se'),
  deepfinN: territoryId('deepfin_n'),
  deepfinM: territoryId('deepfin_mid'),
  deepfinS: territoryId('deepfin_s'),
  sharkbayW: territoryId('sharkbay_w'),
  sharkbayM: territoryId('sharkbay_mid'),
  sharkbayE: territoryId('sharkbay_e'),
  tidewallN: territoryId('tidewall_n'),
  tidewallM: territoryId('tidewall_mid'),
  tidewallS: territoryId('tidewall_s'),
} as const;

// Region bonuses paid out at the start of the deploy phase to a player
// who controls every tile in the region. Coral Reach (4 tiles, 2x2 with
// only 2 cross-region exits) is the tightest, most defensible region —
// it pays +3. The three column/row regions (3 tiles each) pay +2.
//
// Total possible pool = 9. Controlling 1 region is a meaningful but not
// runaway boost on top of the base `max(3, floor(owned/3))` reinforcement
// floor; controlling all 4 means the holder is already winning.
const regions: readonly Region[] = [
  { id: CORAL, name: 'Coral Reach', territoryBonus: 3 },
  { id: DEEPFIN, name: 'Deepfin', territoryBonus: 2 },
  { id: SHARKBAY, name: 'Sharkbay', territoryBonus: 2 },
  { id: TIDEWALL, name: 'Tidewall', territoryBonus: 2 },
];

const territories: readonly Territory[] = [
  // Coral Reach — 2x2 block top-center. Centers spaced 80px apart so
  // adjacent squares share edges.
  {
    id: T.coralNW,
    name: 'Coral NW',
    region: CORAL,
    position: { x: 320, y: 330 },
    adjacent: [T.coralNE, T.coralSW],
  },
  {
    id: T.coralNE,
    name: 'Coral NE',
    region: CORAL,
    position: { x: 400, y: 330 },
    adjacent: [T.coralNW, T.coralSE],
  },
  {
    id: T.coralSW,
    name: 'Coral SW',
    region: CORAL,
    position: { x: 320, y: 410 },
    adjacent: [T.coralNW, T.coralSE, T.tidewallN],
  },
  {
    id: T.coralSE,
    name: 'Coral SE',
    region: CORAL,
    position: { x: 400, y: 410 },
    adjacent: [T.coralNE, T.coralSW, T.deepfinN],
  },

  // Tidewall — vertical column on the left.
  {
    id: T.tidewallN,
    name: 'Tidewall N',
    region: TIDEWALL,
    position: { x: 120, y: 510 },
    adjacent: [T.tidewallM, T.coralSW],
  },
  {
    id: T.tidewallM,
    name: 'Tidewall Mid',
    region: TIDEWALL,
    position: { x: 120, y: 590 },
    adjacent: [T.tidewallN, T.tidewallS],
  },
  {
    id: T.tidewallS,
    name: 'Tidewall S',
    region: TIDEWALL,
    position: { x: 120, y: 670 },
    adjacent: [T.tidewallM, T.sharkbayW],
  },

  // Deepfin — vertical column on the right.
  {
    id: T.deepfinN,
    name: 'Deepfin N',
    region: DEEPFIN,
    position: { x: 600, y: 510 },
    adjacent: [T.deepfinM, T.coralSE],
  },
  {
    id: T.deepfinM,
    name: 'Deepfin Mid',
    region: DEEPFIN,
    position: { x: 600, y: 590 },
    adjacent: [T.deepfinN, T.deepfinS],
  },
  {
    id: T.deepfinS,
    name: 'Deepfin S',
    region: DEEPFIN,
    position: { x: 600, y: 670 },
    adjacent: [T.deepfinM, T.sharkbayE],
  },

  // Sharkbay — horizontal row across the bottom-center.
  {
    id: T.sharkbayW,
    name: 'Sharkbay W',
    region: SHARKBAY,
    position: { x: 280, y: 770 },
    adjacent: [T.sharkbayM, T.tidewallS],
  },
  {
    id: T.sharkbayM,
    name: 'Sharkbay Mid',
    region: SHARKBAY,
    position: { x: 360, y: 770 },
    adjacent: [T.sharkbayW, T.sharkbayE],
  },
  {
    id: T.sharkbayE,
    name: 'Sharkbay E',
    region: SHARKBAY,
    position: { x: 440, y: 770 },
    adjacent: [T.sharkbayM, T.deepfinS],
  },
];

export const ringMap: MapDefinition = {
  id: 'ring',
  name: 'Ring',
  regions,
  territories,
};
